import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, UserPlus, CheckCircle2, Building2, ChevronRight, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { WebService } from "@/service/webService";

// ── Types ─────────────────────────────────────────────────────────────────────
type Teacher = {
    uid: string;
    name: string;
    employee_id: string;
    email: string;
    profile_pic: string | null;
    role: string;
};

type Props = {
    onSelect: (t: Teacher) => void;
    selectedName?: string;
};

// ── Singleton ─────────────────────────────────────────────────────────────────
const webService = new WebService();

// ── Component ─────────────────────────────────────────────────────────────────
export function TeacherSelectionDialog({ onSelect, selectedName }: Props) {
    const [ open, setOpen ] = useState(false);
    const [ teachers, setTeachers ] = useState<Teacher[]>([]);
    const [ search, setSearch ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const [ hasMore, setHasMore ] = useState(true);
    const [ nextToken, setNextToken ] = useState<string | null>(null);
    const [ hasError, setHasError ] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loadingRef = useRef(false); // avoid stale closure in fetchFaculty

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchFaculty = useCallback(async (
        query: string,
        cursor: string | null,
        reset: boolean
    ) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setIsLoading(true);
        if (reset) setHasError(false);

        try {
            const result = await (webService as any).fetchEntities({
                page_size: 6,
                last_pagination_value: cursor ?? null,
                entity_search: query,
                query_entity_type: 'Teachers',
                only_unverified: false,
                sort_by_date: false,
                role: null,
            });

            if (result.success) {
                const incoming = (result.data.results ?? []) as Teacher[];
                setTeachers(prev => reset ? incoming : [ ...prev, ...incoming ]);
                setNextToken(result.data.nextPageToken ?? null);
                setHasMore(!!result.data.nextPageToken);
                setHasError(false);
            } else {
                setHasError(true);
            }
        } catch (e) {
            console.error('fetchFaculty error:', e);
            setHasError(true);
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, []);

    // ── Open — reset + initial load ───────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        setTeachers([]);
        setNextToken(null);
        setHasMore(true);
        setSearch('');
        fetchFaculty('', null, true);
    }, [ open ]);

    // ── Debounced search ──────────────────────────────────────────────────────
    const handleSearch = (q: string) => {
        setSearch(q);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setTeachers([]);
            setNextToken(null);
            setHasMore(true);
            fetchFaculty(q, null, true);
        }, 350);
    };

    // ── Clear search ──────────────────────────────────────────────────────────
    const clearSearch = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setSearch('');
        setTeachers([]);
        setNextToken(null);
        setHasMore(true);
        fetchFaculty('', null, true);
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getInitials = (name: string) =>
        name.split(' ').map(n => n[ 0 ]).join('').substring(0, 2).toUpperCase();

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-3 border-dashed border-primary/30 hover:bg-primary/5 text-primary gap-2"
                >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                        {selectedName ? "Change" : "Assign"}
                    </span>
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden gap-0 border-border/60 shadow-2xl">
                <DialogHeader className="p-4 border-b bg-muted/30">
                    <DialogTitle className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5" />
                        Faculty Directory
                    </DialogTitle>
                </DialogHeader>

                {/* Search */}
                <div className="p-4 border-b">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
                        <Input
                            placeholder="Search by name..."
                            className="pl-10 pr-9 h-12 text-sm bg-muted/10 border-border/40 focus-visible:ring-primary/20"
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            autoFocus
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                aria-label="Clear search"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* List */}
                <div
                    ref={scrollRef}
                    className="max-h-[380px] overflow-y-auto p-2 scrollbar-thin"
                >
                    {teachers.length > 0 ? (
                        <div className="space-y-1">
                            {teachers.map((teacher) => (
                                <button
                                    key={teacher.uid}
                                    onClick={() => {
                                        onSelect(teacher);
                                        setOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-primary/5 transition-all text-left group border border-transparent hover:border-primary/10"
                                >
                                    <Avatar className="h-10 w-10 border-2 border-background shadow-sm">
                                        <AvatarImage src={teacher.profile_pic ?? ''} />
                                        <AvatarFallback className="text-[10px] font-bold bg-muted group-hover:bg-primary group-hover:text-white uppercase transition-colors">
                                            {getInitials(teacher.name)}
                                        </AvatarFallback>
                                    </Avatar>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-bold tracking-tight truncate">
                                                {teacher.name}
                                            </p>
                                            {selectedName === teacher.name && (
                                                <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter mt-0.5">
                                            ID: {teacher.employee_id} • {teacher.email}
                                        </p>
                                    </div>

                                    <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-30 transition-all transform group-hover:translate-x-1" />
                                </button>
                            ))}

                            {/* Pagination footer */}
                            {isLoading ? (
                                <div className="flex justify-center py-4">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            ) : hasMore ? (
                                <div className="pt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="w-full text-xs"
                                        onClick={() => fetchFaculty(search, nextToken, false)}
                                    >
                                        Load more
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-center text-[10px] text-muted-foreground py-3 uppercase font-black tracking-widest">
                                    All faculty loaded
                                </p>
                            )}
                        </div>
                    ) : isLoading ? (
                        <div className="py-12 flex justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/40" />
                        </div>
                    ) : hasError ? (
                        <div className="py-12 text-center">
                            <p className="text-[10px] font-black uppercase tracking-widest text-destructive mb-3">
                                Couldn't load faculty — try again
                            </p>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fetchFaculty(search, null, true)}
                            >
                                Retry
                            </Button>
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <Search className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                                No faculty found
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}